import React from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { marked } from "marked";
import DOMPurify from "dompurify";
import { ChevronRight, CalendarDays, Clock, User, ArrowLeft } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { CTABand } from "@/components/CTABand";
import { FaqSection } from "@/components/FaqSection";
import { applyHead } from "@/components/Seo";
import { headForPost } from "@/content/seo";
import { api } from "@/lib/api";
import { fmtDate } from "@/pages/Insights";

marked.setOptions({ gfm: true, breaks: false });

export default function Article() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = React.useState(null);
  const [state, setState] = React.useState("loading");

  React.useEffect(() => {
    let alive = true;
    setState("loading");
    api
      .get(`/posts/${slug}`)
      .then((r) => {
        if (!alive) return;
        setPost(r.data);
        setState("ready");
        applyHead(headForPost(r.data));
      })
      .catch(() => alive && setState("missing"));
    return () => {
      alive = false;
    };
  }, [slug]);

  if (state === "missing") {
    return (
      <div className="mx-auto max-w-3xl px-5 pb-24 pt-40 text-center sm:px-8" data-testid="article-missing">
        <h1 className="font-display text-3xl font-bold text-white">Article not found</h1>
        <p className="mt-4 text-[#DDE3F2]">This article may have been renamed or unpublished.</p>
        <button onClick={() => navigate("/insights")} className="btn-primary mt-8" data-testid="article-back-btn">
          Back to Insights
        </button>
      </div>
    );
  }

  if (state === "loading") {
    return (
      <div className="mx-auto max-w-3xl px-5 pb-24 pt-40 sm:px-8" data-testid="article-loading">
        <div className="h-4 w-40 animate-pulse rounded bg-white/10" />
        <div className="mt-6 h-10 w-full animate-pulse rounded bg-white/10" />
        <div className="mt-3 h-10 w-3/4 animate-pulse rounded bg-white/10" />
      </div>
    );
  }

  const html = DOMPurify.sanitize(marked.parse(post.body || ""));

  return (
    <div data-testid={`article-page-${post.slug}`}>
      <article className="relative overflow-hidden">
        <div className="glow-orb -left-40 top-24 h-[420px] w-[420px] bg-[#8E9AEA]/10" aria-hidden />
        <div className="mx-auto max-w-3xl px-5 pb-12 pt-28 sm:px-8 sm:pt-32 lg:pt-36">
          <nav
            className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] font-medium uppercase tracking-[0.16em] text-[#A6B0C9] sm:text-xs"
            data-testid="article-breadcrumb"
          >
            <Link to="/" className="transition-colors duration-200 hover:text-white">
              Home
            </Link>
            <ChevronRight className="h-3 w-3 text-[#A6B0C9]/60" />
            <Link to="/insights" className="transition-colors duration-200 hover:text-white">
              Insights
            </Link>
            <ChevronRight className="h-3 w-3 text-[#A6B0C9]/60" />
            <span className="text-[#DDE3F2]">{(post.tags || [])[0] || "Article"}</span>
          </nav>

          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-5 font-display text-[1.7rem] font-extrabold leading-[1.18] tracking-tight text-white sm:mt-6 sm:text-[2.1rem] lg:text-[2.5rem] lg:leading-[1.14]"
            data-testid="article-title"
          >
            {post.title}
          </motion.h1>

          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 border-y border-white/10 py-4 text-[11px] uppercase tracking-[0.14em] text-[#A6B0C9]">
            <span className="inline-flex items-center gap-1.5">
              <User className="h-3.5 w-3.5 text-[#8E9AEA]" />
              {post.author}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <CalendarDays className="h-3.5 w-3.5 text-[#8E9AEA]" />
              {fmtDate(post.publishedAt)}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5 text-[#8E9AEA]" />
              {post.readingMinutes} min read
            </span>
          </div>

          <Reveal>
            {/* Body is authored in markdown by Lanvaus staff and sanitised before render. */}
            <div
              className="prose-lanvaus mt-10"
              data-testid="article-body"
              dangerouslySetInnerHTML={{ __html: html }}
            />
          </Reveal>

          <div className="mt-12 flex flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-6">
            <Link
              to="/insights"
              className="inline-flex items-center gap-2 text-sm font-semibold text-white transition-colors duration-200 hover:text-[#8E9AEA]"
              data-testid="article-back-link"
            >
              <ArrowLeft className="h-4 w-4" /> All insights
            </Link>
            <Link to="/services/open-source-soc" className="btn-secondary" data-testid="article-soc-link">
              See the Open Source SOC Model
            </Link>
          </div>
        </div>
      </article>

      <FaqSection faqs={post.faqs} title="Related questions" eyebrow="FAQ" />

      <CTABand
        title={{ pre: "Need this mapped to ", accent: "your systems?" }}
        copy="A Lanvaus engineer will walk your current setup against the obligations in this article and tell you what is missing — no obligation."
      />
    </div>
  );
}
